import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Upload } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { api, formatApiErrorDetail, API } from "@/lib/api";

const SKIN_TYPES = ["normal", "seca", "oleosa", "mista", "sensivel", "acneica"];
const STATUS_LABELS = { pending: "Pendente", confirmed: "Confirmado", completed: "Concluído", cancelled: "Cancelado" };

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`/admin/clients/${id}`).then((r) => setClient(r.data)).catch(() => {});

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const { appointments, photos, id: cid, created_at, ...body } = client;
      await api.put(`/admin/clients/${id}`, body);
      toast.success("Dados atualizados");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const uploadPhoto = async (label) => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Selecione uma foto");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("label", label);
    try {
      await api.post(`/admin/clients/${id}/photos?label=${label}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto enviada");
      fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!client) return <p className="text-[#6F3F55]">Carregando...</p>;

  return (
    <div data-testid="client-detail-page">
      <button onClick={() => navigate("/admin/clientes")} className="flex items-center gap-1 text-sm text-[#6F3F55] mb-4" data-testid="client-detail-back">
        <ChevronLeft className="w-4 h-4" /> Voltar
      </button>
      <h1 className="text-2xl font-serif text-[#9B6179] mb-6">{client.first_name} {client.last_name}</h1>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-[2px] p-6 shadow-sm border border-[#9B6179]/5 space-y-3">
          <p className="text-sm font-medium text-[#9B6179] mb-2">Ficha da Cliente</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nome</Label><Input value={client.first_name} onChange={(e) => setClient({ ...client, first_name: e.target.value })} data-testid="client-detail-firstname" /></div>
            <div><Label>Sobrenome</Label><Input value={client.last_name} onChange={(e) => setClient({ ...client, last_name: e.target.value })} data-testid="client-detail-lastname" /></div>
          </div>
          <div><Label>E-mail</Label><Input value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} data-testid="client-detail-email" /></div>
          <div><Label>WhatsApp</Label><Input value={client.whatsapp} onChange={(e) => setClient({ ...client, whatsapp: e.target.value })} data-testid="client-detail-whatsapp" /></div>
          <div>
            <Label>Tipo de pele</Label>
            <Select value={client.skin_type} onValueChange={(v) => setClient({ ...client, skin_type: v })}>
              <SelectTrigger data-testid="client-detail-skintype"><SelectValue /></SelectTrigger>
              <SelectContent>{SKIN_TYPES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Notas de enfermagem</Label><Textarea rows={4} value={client.notes || ""} onChange={(e) => setClient({ ...client, notes: e.target.value })} data-testid="client-detail-notes" /></div>
          <Button disabled={saving} onClick={save} className="w-full bg-[#090708] text-[#E8C47A] rounded-[2px] hover:bg-[#6F3F55] transition-colors" data-testid="client-detail-save">Salvar Alterações</Button>
        </div>

        <div className="bg-white rounded-[2px] p-6 shadow-sm border border-[#9B6179]/5 lg:col-span-2">
          <p className="text-sm font-medium text-[#9B6179] mb-4">Histórico de Atendimentos</p>
          <div className="space-y-3">
            {(client.appointments || []).length === 0 && <p className="text-sm text-[#6F3F55]">Nenhum atendimento registrado.</p>}
            {(client.appointments || []).map((a) => (
              <div key={a.id} className="border border-[#9B6179]/10 rounded-[2px] p-3" data-testid={`client-appointment-${a.id}`}>
                <div className="flex justify-between items-center">
                  <p className="text-sm font-medium text-[#9B6179]">{a.date} — {a.slot}</p>
                  <Badge variant="outline">{STATUS_LABELS[a.status] || a.status}</Badge>
                </div>
                {a.laser_details && (a.laser_details.session_number || a.laser_details.pch || a.laser_details.valor) && (
                  <p className="text-xs text-[#6F3F55] mt-1">
                    Sessão {a.laser_details.session_number || "-"} · PCH: {a.laser_details.pch || "-"} · Valor: {a.laser_details.valor ? `${a.laser_details.valor}€` : "-"}
                  </p>
                )}
                {a.notes && <p className="text-xs text-[#594A50] mt-1">{a.notes}</p>}
              </div>
            ))}
          </div>

          <p className="text-sm font-medium text-[#9B6179] mt-8 mb-4">Galeria Antes/Depois (privada)</p>
          <div className="flex items-center gap-2 mb-4">
            <input ref={fileRef} type="file" accept="image/*" data-testid="client-photo-file-input" className="text-sm" />
            <Button size="sm" variant="outline" onClick={() => uploadPhoto("antes")} data-testid="client-upload-before">+ Antes</Button>
            <Button size="sm" variant="outline" onClick={() => uploadPhoto("depois")} data-testid="client-upload-after">+ Depois</Button>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {(client.photos || []).map((p) => (
              <div key={p.id} className="relative" data-testid={`client-photo-${p.id}`}>
                <img src={`${API}/admin/clients/photos/${p.id}/download`} alt={p.label} className="w-full h-28 object-cover rounded-[2px]" />
                <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-[2px] capitalize">{p.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
